import { useCallback, useRef, useState } from "react";

export function isVoiceRecordingSupported() {
  return typeof navigator !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia) && typeof MediaRecorder !== "undefined";
}

// Nível RMS abaixo do qual consideramos "silêncio" - ajustado pra pegar ruído de fundo
// comum de microfone sem cortar fala baixa.
const SILENCE_RMS_THRESHOLD = 0.03;
// Silêncio contínuo necessário pra considerar que a pessoa parou de falar.
const SILENCE_HOLD_MS = 1400;
// Não deixa parar sozinho logo no início (evita cortar antes da pessoa começar a falar).
const MIN_RECORDING_BEFORE_AUTO_STOP_MS = 1200;

interface UseVoiceRecorderOptions {
  /** Chamado uma vez quando detecta silêncio sustentado após a pessoa falar - evita gravação (e custo de transcrição) rodando à toa. */
  onSilence?: () => void;
}

export function useVoiceRecorder({ onSilence }: UseVoiceRecorderOptions = {}) {
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array<ArrayBuffer> | null>(null);
  const levelRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const silenceStartRef = useRef<number | null>(null);
  const recordingStartRef = useRef(0);
  const onSilenceRef = useRef(onSilence);
  onSilenceRef.current = onSilence;

  /** Nível de volume atual (0-1), lido via ref pra animar sem re-renderizar o componente a cada frame. */
  const getLevel = useCallback(() => levelRef.current, []);

  const analyze = useCallback(() => {
    const analyser = analyserRef.current;
    const data = dataArrayRef.current;
    if (!analyser || !data) return;

    analyser.getByteTimeDomainData(data);
    let sumSquares = 0;
    for (let i = 0; i < data.length; i++) {
      const normalized = (data[i] - 128) / 128;
      sumSquares += normalized * normalized;
    }
    const rms = Math.sqrt(sumSquares / data.length);
    levelRef.current = Math.min(1, rms * 4);

    const now = performance.now();
    if (rms < SILENCE_RMS_THRESHOLD) {
      if (silenceStartRef.current === null) silenceStartRef.current = now;
      const silenceElapsed = now - silenceStartRef.current;
      const recordingElapsed = now - recordingStartRef.current;
      if (silenceElapsed >= SILENCE_HOLD_MS && recordingElapsed >= MIN_RECORDING_BEFORE_AUTO_STOP_MS) {
        onSilenceRef.current?.();
        return;
      }
    } else {
      silenceStartRef.current = null;
    }

    rafRef.current = requestAnimationFrame(analyze);
  }, []);

  const start = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    chunksRef.current = [];

    const audioCtx = new AudioContext();
    const source = audioCtx.createMediaStreamSource(stream);
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);
    audioCtxRef.current = audioCtx;
    analyserRef.current = analyser;
    dataArrayRef.current = new Uint8Array(new ArrayBuffer(analyser.fftSize));
    silenceStartRef.current = null;
    recordingStartRef.current = performance.now();
    rafRef.current = requestAnimationFrame(analyze);

    const recorder = new MediaRecorder(stream);
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    mediaRecorderRef.current = recorder;
    recorder.start();
    setIsRecording(true);
  }, [analyze]);

  const stop = useCallback((): Promise<Blob> => {
    return new Promise((resolve) => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      levelRef.current = 0;
      audioCtxRef.current?.close().catch(() => {});
      audioCtxRef.current = null;
      analyserRef.current = null;

      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state !== "recording") {
        streamRef.current?.getTracks().forEach((track) => track.stop());
        setIsRecording(false);
        resolve(new Blob());
        return;
      }
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
        streamRef.current?.getTracks().forEach((track) => track.stop());
        setIsRecording(false);
        resolve(blob);
      };
      recorder.stop();
    });
  }, []);

  return { isRecording, start, stop, getLevel };
}
